/** Maximum size of a single WebSocket frame on either hop. */
export const MAX_FRAME_BYTES = 8 * 1024 * 1024;
/** Maximum file size the editor will read or write. */
export const MAX_FILE_BYTES = 5 * 1024 * 1024;
/** Maximum length of a workspace-relative path. */
export const MAX_PATH_LENGTH = 4096;
/** Maximum entries returned by a single listDirectory call. */
export const MAX_DIRECTORY_ENTRIES = 5000;
/** Max bytes of a single terminal input notification. */
export const MAX_TERMINAL_INPUT = 64 * 1024;
/** Relay request timeout. */
export const RELAY_REQUEST_TIMEOUT_MS = 60_000;

// ---- AI (milestone M2A) ----------------------------------------------------------------------------------
/** Max model round-trips (tool-loop iterations) per user message. */
export const AI_MAX_ITERATIONS = 8;
/** Max tool calls executed per run / per model turn. Extra calls get a structured error result. */
export const AI_MAX_TOOL_CALLS_PER_RUN = 20;
export const AI_MAX_TOOL_CALLS_PER_TURN = 5;
/** Max concurrent runs per agent. */
export const AI_MAX_CONCURRENT_RUNS = 4;
/** Max characters of one tool result sent to the model. */
export const AI_MAX_TOOL_RESULT_CHARS = 30_000;
/** Deterministic context budgets (characters, roughly 4 per token). */
export const AI_HISTORY_BUDGET_CHARS = 120_000;
export const AI_RUN_CONTEXT_BUDGET_CHARS = 240_000;
/** Chat messages the server replays to the agent as history. */
export const AI_HISTORY_MAX_MESSAGES = 40;
export const AI_MAX_USER_MESSAGE_CHARS = 20_000;

// ---- File Transfer (milestone M5) ------------------------------------------------------------------------
/** Maximum size of a single uploaded file (100 MiB). */
export const MAX_UPLOAD_FILE_BYTES = 100 * 1024 * 1024;
/** Maximum number of files in a single batch upload. */
export const MAX_UPLOAD_BATCH_FILES = 100;
/** Maximum total bytes across a single batch upload (500 MiB). */
export const MAX_UPLOAD_BATCH_TOTAL_BYTES = 500 * 1024 * 1024;
/** Size of a single streaming chunk (256 KiB). */
export const MAX_UPLOAD_CHUNK_BYTES = 256 * 1024;
/** Maximum concurrent active transfers per project on the agent. */
export const MAX_CONCURRENT_TRANSFERS_PER_PROJECT = 5;
/** Inactivity timeout for an active transfer before it is automatically cleaned up (60s). */
export const TRANSFER_INACTIVITY_TIMEOUT_MS = 60_000;
/** High-water mark for WebSocket bufferedAmount before pausing chunk transmission (512 KiB). */
export const MAX_BUFFERED_AMOUNT_THRESHOLD = 512 * 1024;

// ---- Project Context References (milestone M5.1-A) -------------------------------------------------------
/** Maximum characters in a single editor selection snapshot. */
export const MAX_SELECTION_CHARS = 50_000;
/** Maximum context reference items attached to a single chat message. */
export const MAX_CONTEXT_ITEMS_PER_MESSAGE = 30;
/** Maximum size of an individual file attached as project context (512 KiB). */
export const MAX_CONTEXT_FILE_BYTES = 512 * 1024;
/** Maximum number of files included per folder reference. */
export const MAX_FOLDER_FILES = 50;
/** Maximum size of an individual file within a folder reference (256 KiB). */
export const MAX_FOLDER_FILE_BYTES = 256 * 1024;
/** Maximum aggregate bytes across all files in a folder reference (1 MiB). */
export const MAX_FOLDER_TOTAL_BYTES = 1024 * 1024;
/** Maximum directory nesting depth for folder expansion. */
export const MAX_FOLDER_DEPTH = 8;

// ---- Chat Attachments (milestone M5.1-B) ------------------------------------------------------------------
/** Maximum size of an individual attachment (10 MiB for multimodal). */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
/** Maximum size of a text attachment (512 KiB). */
export const MAX_TEXT_ATTACHMENT_BYTES = 512 * 1024;
/** Maximum number of attachments attached to a single prompt. */
export const MAX_ATTACHMENTS_PER_PROMPT = 10;
/** TTL for orphaned unsent attachments in the agent attachment store (2 hours). */
export const ATTACHMENT_ORPHAN_TTL_MS = 2 * 60 * 60 * 1000;

// ---- Context Assembly & Global Budgeting (milestone M5.1-C) ----------------------------------------------
/** Maximum aggregate textual context bytes across all referenced items in a prompt (1.5 MiB). */
export const MAX_AGGREGATE_CONTEXT_BYTES = 1.5 * 1024 * 1024;




// ---- LSP / IntelliSense (milestone M6.1) -----------------------------------------------------------------
/** Maximum concurrently running language-server processes on one Machine (a browser cannot fork-bomb it). */
export const LSP_MAX_SESSIONS = 8;
/** Grace period after a browser socket closes before its language servers are stopped by the relay. */
export const LSP_DISCONNECT_GRACE_MS = 3000;
/** Bytes of language-server stderr kept per session (for diagnostics of crashes only; never sent to the browser). */
export const LSP_MAX_STDERR_BYTES = 64 * 1024;
/** Largest single JSON-RPC message accepted from a language server (larger ones terminate the session). */
export const LSP_MAX_MESSAGE_BYTES = 16 * 1024 * 1024;
/** Diagnostics kept per file / in total by the browser (UI safety). */
export const LSP_MAX_DIAGNOSTICS_PER_FILE = 200;
export const LSP_MAX_DIAGNOSTICS_TOTAL = 2000;

// ---- AI Autocomplete (milestone M6.2) ---------------------------------------------------------------------
/** Maximum characters of prefix before cursor sent to provider (~12 KiB). */
export const AUTOCOMPLETE_MAX_PREFIX_CHARS = 12 * 1024;
/** Maximum characters of suffix after cursor sent to provider (~6 KiB). */
export const AUTOCOMPLETE_MAX_SUFFIX_CHARS = 6 * 1024;
/** Maximum total characters across prefix and suffix (~20 KiB). */
export const AUTOCOMPLETE_MAX_TOTAL_CONTEXT_CHARS = 20 * 1024;
/** Maximum tokens for autocomplete model generation. */
export const AUTOCOMPLETE_MAX_OUTPUT_TOKENS = 256;
/** Hard response character cap to bound memory and prevent runaway text. */
export const AUTOCOMPLETE_HARD_RESPONSE_CHAR_CAP = 4096;
/** Idle typing debounce duration before triggering an autocomplete request (ms). */
export const AUTOCOMPLETE_DEBOUNCE_MS = 500;
/** Ephemeral request timeout for autocomplete provider calls (ms). */
export const AUTOCOMPLETE_TIMEOUT_MS = 10_000;

